import { memo, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { BUTTON_LABELS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IAccountDetailsResponse, IVerticalTabProps } from "@miplatform/types";
import { VerticalTabsLayout } from "@mibuildweb/components";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import { CustomerOptLineItemsTab } from "../tabs/CustomerLineItemsTabs/CustomerOptLineItemsTab";
import {
  CustomerBidLineItemsTab,
  CustomerDrawsDistributionTab,
  CustomerDrawsSetupTab,
  CustomerFloorsDropsTab,
  CustomerItemsAssignTab,
  CustomerItemsPricingTab,
  CustomerPriceLevelTab,
} from "../tabs/index";
import { CustomerPricingDetailsTabs } from "./CustomerPricingDetails.types";
import {
  CustomerPricingDetailsProvider,
  useCustomerPricingDetailsContext,
} from "./CustomerPricingDetailsProvider";

function CustomerPricingDetailsContent() {
  const {
    accountDetails,
    isViewModelLoading,
    tabFormState,
    handleSave,
    handleCancel,
  } = useCustomerPricingDetailsContext();

  const { params, navigate } = useRouter();
  const selectedTab = params.tab as CustomerPricingDetailsTabs;

  const handleTabChange = (
    item: IVerticalTabProps<IAccountDetailsResponse>
  ) => {
    if (!item.disabled) {
      navigate(`/estimating/pricing/customers/${params.id}/${item.value}`);
    }
  };

  const tabs = useMemo<IVerticalTabProps<IAccountDetailsResponse>[]>(
    () => [
      {
        label: "Price Level",
        value: CustomerPricingDetailsTabs.PriceLevel,
        viewComponent: CustomerPriceLevelTab,
      },
      {
        label: "Floors & Drops",
        value: CustomerPricingDetailsTabs.FloorsDrops,
        viewComponent: CustomerFloorsDropsTab,
      },
      {
        label: "Draws Setup",
        value: CustomerPricingDetailsTabs.DrawsSetup,
        viewComponent: CustomerDrawsSetupTab,
      },
      {
        label: "Draws Distribution",
        value: CustomerPricingDetailsTabs.DrawsDistribution,
        viewComponent: CustomerDrawsDistributionTab,
      },
      {
        label: "Bid Line Items",
        value: CustomerPricingDetailsTabs.BidLineItems,
        viewComponent: CustomerBidLineItemsTab,
      },
      {
        label: "Option Line Items",
        value: CustomerPricingDetailsTabs.OptionLineItems,
        viewComponent: CustomerOptLineItemsTab,
      },
      {
        label: "Items",
        value: CustomerPricingDetailsTabs.Items,
        viewComponent: CustomerItemsAssignTab,
      },
      {
        label: "Items Pricing",
        value: CustomerPricingDetailsTabs.ItemsPricing,
        viewComponent: CustomerItemsPricingTab,
      },
    ],
    []
  );

  const isLoading = isViewModelLoading || tabFormState.isLoading;

  const actions = useMemo(() => {
    return {
      cancel: {
        label: BUTTON_LABELS.CANCEL,
        onClick: handleCancel,
      },
      submit: {
        label: BUTTON_LABELS.SAVE,
        onClick: handleSave,
        disabled: !tabFormState.isDirty || !tabFormState.isValid || isLoading,
      },
    };
  }, [tabFormState, isLoading, handleSave, handleCancel]);

  if (!tabs.some((tab) => tab.value === selectedTab)) {
    return (
      <Navigate
        to={`/estimating/pricing/customers/${params.id}/${CustomerPricingDetailsTabs.PriceLevel}`}
        replace
      />
    );
  }

  const renderTitle = () => {
    return accountDetails ? `${accountDetails.name}` : "";
  };

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      loading={isLoading}
      bottomActions={actions}
    >
      <VerticalTabsLayout
        tabs={tabs}
        value={selectedTab}
        onTabChange={handleTabChange}
        transparentBackground
        hideNavigationButtons
      />
    </ConfigPageLayout>
  );
}

export const CustomerPricingDetails = memo(() => {
  return (
    <CustomerPricingDetailsProvider>
      <CustomerPricingDetailsContent />
    </CustomerPricingDetailsProvider>
  );
});
